import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Icon, {
  CloseOutlined,
  LogoutOutlined,
  MailOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { Divider } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { sessionSelectors } from 'store/state/selectors';
import {
  FeatureFlags,
  useFeatureFlagStore,
} from 'zustand/featureFlag/useFeatureFlagStore';
import * as BuyIntentionCalls from 'store/state/buyIntention/apiCalls';
import { buyIntentionActions, sessionActions } from 'store/state/slices';
import { HeaderEnum } from 'enums/header.enum';
import { getRefreshTokenService, setLogout } from 'services/config';
import { logoutSession } from 'services/sessions/SessionsService';
import { ReactComponent as supportAgent } from 'assets/svgs/supportAgent.svg';
import { ReactComponent as overview } from 'assets/svgs/overview.svg';
import { ReactComponent as receiptLong } from 'assets/svgs/receiptLong.svg';
import { ReactComponent as openInNew } from 'assets/svgs/openInNew.svg';
import * as S from './styles';

interface Props {
  isOpen: boolean;
  setOpen: (arg0: boolean) => void;
}

function MobileMenu({ isOpen, setOpen }: Props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { name: userName, producerUrl } = useSelector(sessionSelectors.getRoot);
  const { t } = useTranslation('translation', { keyPrefix: 'header' });

  const isFeatureFlagEnabled = useFeatureFlagStore(
    (state) => state.isFeatureFlagEnabled,
  );

  const isDeskManagerServiceRequestsEnabled = isFeatureFlagEnabled(
    FeatureFlags.DeskManagerServiceRequestsOnECommerce,
  );

  const isProducerAuthenticated = useSelector(
    sessionSelectors.isProducerAuthenticated,
  );

  const handleClickMyOrders = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/${producerUrl}/my-orders`);
  };

  const handleClickMyOrderIntentions = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/${producerUrl}/my-order-intentions`);
  };

  const handleClickServiceRequest = () => {
    if (!isDeskManagerServiceRequestsEnabled) {
      window.open(HeaderEnum.LINKSUPPORT);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(`/${producerUrl}/service-request`);
    }
  };

  const handleClickBuyIntention = () => {
    dispatch(buyIntentionActions.setShowIntentionModal(true));
    dispatch(BuyIntentionCalls.fetchProducers());
  };

  const handleContactsButtonClick = () => {
    window.open(HeaderEnum.LINKBUSINESSCONTACTS);
  };

  const logout = useCallback(async () => {
    const refresh = getRefreshTokenService();
    if (refresh) {
      try {
        await logoutSession(refresh);
      } catch {
        // best-effort: continua mesmo se backend falhar
      }
    }
    setLogout();
    dispatch(sessionActions.logout());
    navigate('/logout');
  }, [dispatch, navigate]);

  const onClose = () => {
    setOpen(false);
  };

  return (
    <S.MobileMenu
      width={280}
      placement="right"
      onClose={onClose}
      closable={false}
      visible={isOpen}
      title={
        <S.Header>
          <S.CloseButton onClick={onClose}>
            <CloseOutlined />
          </S.CloseButton>
          <S.Text small>{t('veilingOnline')}</S.Text>
          <S.Logo />
        </S.Header>
      }
    >
      <S.WelcomeMessage>
        <S.Text small gray>
          {t('welcome')}
        </S.Text>
        <S.UserName>{userName}</S.UserName>
      </S.WelcomeMessage>
      {!isProducerAuthenticated && (
        <S.MenuItem key="drop-down-actions-1" onClick={handleClickMyOrders}>
          <Icon component={overview} />
          <S.Text>{t('myOrders')}</S.Text>
        </S.MenuItem>
      )}
      <S.MenuItem
        key="drop-down-actions-5"
        onClick={handleClickMyOrderIntentions}
      >
        <Icon component={receiptLong} />
        <S.Text>{t('myOrderIntentions')}</S.Text>
      </S.MenuItem>
      <S.MenuItem key="drop-down-actions-4" onClick={handleClickBuyIntention}>
        <ShoppingCartOutlined />
        <S.Text>{t('registerOrderIntention')}</S.Text>
      </S.MenuItem>
      <S.MenuItem onClick={handleClickServiceRequest} key="drop-down-actions-2">
        <Icon component={supportAgent} />
        <S.Text>{t('openRequests')}</S.Text>
      </S.MenuItem>
      <Divider />
      <S.MenuItem onClick={handleContactsButtonClick} key="drop-down-actions-3">
        <MailOutlined />
        <S.Text>{t('veilingContact')}</S.Text>
        <S.Logout component={openInNew} onClick={logout} />
      </S.MenuItem>
      <Divider />
      <S.LogoutButton onClick={logout}>
        Encerrar sessão <LogoutOutlined color="white" />
      </S.LogoutButton>
    </S.MobileMenu>
  );
}

export default memo(MobileMenu);
